import { Head, Link } from '@inertiajs/react'

import ExpandDetails from '../components/ExpandDetails'
import MermaidDiagram from '../components/MermaidDiagram'
import { useTheme } from '../hooks/useTheme'

const DIAGRAM_SYSTEM = `flowchart TB
  subgraph client["Browser"]
    I[Inertia + React]
    V[Vite-built assets]
  end
  subgraph srv["Rails"]
    C[Controllers / views]
    M[Models and services]
  end
  DB[(PostgreSQL)]
  I <-->|JSON page visits| C
  V --> I
  C --> M
  M --> DB`

const DIAGRAM_VISIT = `sequenceDiagram
  actor User
  participant IC as Inertia client
  participant RC as Rails
  User->>IC: Visit URL / form
  IC->>RC: Request + Inertia headers
  RC->>RC: Session + domain logic
  RC->>IC: Component name + props JSON
  IC->>User: React renders page`

const diagramPairGrid =
  'not-prose my-6 grid grid-cols-1 gap-6 lg:my-8 lg:grid-cols-2 lg:items-start lg:gap-8 xl:gap-10'

const diagramPairText =
  'min-w-0 text-base leading-relaxed text-on-surface/94 md:text-[17px] md:leading-relaxed [&_strong]:font-semibold [&_strong]:text-on-surface'

const diagramPairFigure = 'min-w-0 w-full justify-self-center lg:justify-self-stretch'

export default function About() {
  const { theme } = useTheme()
  const proseScheme = theme === 'dark' ? 'prose-invert' : ''

  const prose =
    `prose prose-lg max-w-none text-on-surface/92 ${proseScheme} ` +
    'prose-headings:font-headline prose-headings:tracking-tight prose-headings:text-on-surface ' +
    'prose-h2:mb-4 prose-h2:mt-12 prose-h2:border-b prose-h2:border-outline-variant/15 prose-h2:pb-3 prose-h2:text-2xl prose-h2:font-bold ' +
    'prose-h3:mb-2 prose-h3:mt-8 prose-h3:text-lg prose-h3:font-semibold ' +
    'prose-p:font-body prose-p:leading-relaxed prose-p:text-on-surface/94 ' +
    'prose-ul:font-body prose-li:marker:text-primary-container ' +
    'prose-strong:text-on-surface prose-a:text-primary-container prose-a:underline prose-a:underline-offset-2 ' +
    'prose-code:rounded-sm prose-code:bg-surface-container-lowest prose-code:px-1.5 prose-code:py-0.5 prose-code:text-on-surface'

  return (
    <>
      <Head title="About this site" />
      <article className={`mx-auto max-w-5xl pb-16 md:pb-24 ${prose}`}>
        <p className="!mt-0 font-mono text-[12px] font-bold tracking-widest text-primary uppercase">
          Technical overview
        </p>
        <h1 className="!mt-2 !mb-6 text-3xl font-bold tracking-tight text-on-surface md:text-4xl">
          How Nogrok is built
        </h1>
        <p className="lead text-on-surface-variant !mb-8 text-lg">
          Nogrok is a <strong>Rails</strong> app. <strong>Inertia.js</strong> hands page data to <strong>React</strong> in
          the browser. The client uses TypeScript, <strong>Vite</strong>, and <strong>Tailwind CSS</strong>.{' '}
          <strong>PostgreSQL</strong> holds persistent data. The rest of this page walks through the architecture, how a
          visit moves through the stack, the tools that run only in the browser (including the sandbox), and feature
          flags.
        </p>

        <h2 id="architecture">Architecture</h2>
        <div className={diagramPairGrid}>
          <ul className={`${diagramPairText} !mt-0 mb-0 list-none space-y-4 pl-0 [&>li]:pl-0`}>
            <li>
              <strong>Rails.</strong> Routes, controllers, models, sessions, persistence, and the final word on what data
              leaves the server.
            </li>
            <li>
              <strong>Inertia.js.</strong> Controllers return JSON with the page component and <strong>props</strong>,
              not a full HTML document on each navigation, so the client can swap views without a full reload.
            </li>
            <li>
              <strong>React.</strong> Renders the browser UI. Mounts the root component that Inertia selects for each
              visit.
            </li>
            <li>
              <strong>Vite.</strong> TypeScript and CSS for dev and production builds.
            </li>
            <li>
              <strong>Propshaft.</strong> Serves static assets that live outside the Vite bundle, so Vite's compiled
              output and other static files stay separate.
            </li>
            <li>
              <strong>PostgreSQL.</strong> Used in every environment. Active Record migrations track the schema history.
            </li>
          </ul>
          <figure className={diagramPairFigure}>
            <MermaidDiagram
              compact
              chart={DIAGRAM_SYSTEM}
              label="System diagram: browser, Rails, and PostgreSQL"
            />
            <figcaption className="mt-2 text-center font-mono text-[12px] leading-snug text-on-surface-variant">
              One app can handle visits without a public API for every page shape.
            </figcaption>
          </figure>
        </div>

        <ExpandDetails title="Why this shape (monolith + Inertia)">
          <p className="!mt-0">
            One <strong>Rails app</strong> keeps deploy, sessions, and data in one unit. Compared to a{' '}
            <strong>SPA plus a REST or GraphQL API</strong>, Inertia drops the parallel routing and auth model and the
            fragile client or server ship order on field renames.
          </p>
          <p>
            A public API layer still fits when many clients need it. Here the site is the main client, so Rails and
            Inertia can keep the contract private.
          </p>
          <p>
            Compared to <strong>Hotwire (Turbo and Stimulus)</strong> alone, Inertia costs more JavaScript and Node in the
            toolchain but pays off for dense, typed UIs such as the tools, Monaco, and sandbox. Rails still handles every
            navigation.
          </p>
        </ExpandDetails>

        <h2 id="inertia-flow">What happens on each navigation</h2>
        <div className={diagramPairGrid}>
          <div
            className={`${diagramPairText} flex flex-col gap-12 md:gap-14 [&_p]:!m-0 [&_p]:leading-[1.7] md:[&_p]:leading-[1.72]`}
          >
            <p>
              Inertia can take eligible links and forms before a full navigation. The next request is still a normal
              Rails visit: same cookie, middleware, CSRF on posts, and auth as a full page. It is tagged as Inertia so
              the server can return JSON instead of a new HTML shell.
            </p>
            <p>
              That JSON names the <strong>page component</strong> and <strong>props</strong> for the action, including
              flash and validation errors. The outer layout stays put. The browser loads a full HTML document when
              needed: outside Inertia, on some redirects, or as a fallback. Otherwise, most in-app moves stay on the
              short round trip. They feel like an SPA without a hand-written REST contract per screen.
            </p>
          </div>
          <figure className={diagramPairFigure}>
            <MermaidDiagram
              compact
              chart={DIAGRAM_VISIT}
              label="Sequence diagram of an Inertia page transition"
            />
            <figcaption className="mt-2 text-center font-mono text-[12px] leading-snug text-on-surface-variant">
              One request, then render on the client.
            </figcaption>
          </figure>
        </div>

        <ExpandDetails title="Rails stack vs alternatives">
          <p className="!mt-0">
            The fit question is glue for first-party pages, one database, and a few heavy interactive surfaces, not
            abstract framework scores.
          </p>
          <ul className="!mb-0">
            <li>
              <strong>Rails 8.</strong> Conventions, Active Record, jobs, and HTTP behavior are already there so work stays
              on product, not reinventing CRUD and deploy patterns.
            </li>
            <li>
              <strong>Node (Nest or Fastify) or Django.</strong> Credible stacks, but another server runtime here would
              add ops without a clear capability gap.
            </li>
          </ul>
        </ExpandDetails>

        <h2 id="frontend">React, TypeScript, and Vite</h2>
        <div className="flex flex-col gap-12 md:gap-14 [&_p]:!m-0">
          <p>
            Nogrok uses <strong>React 19</strong> and <strong>TypeScript</strong> with typed props from Rails, so
            refactors surface as compile errors. <strong>Vite</strong> builds assets. <strong>vite-plugin-ruby</strong> ties
            entrypoints to Rails so production builds stay routine.
          </p>
          <p>
            TypeScript pins the component contracts. Inertia marks page data as server data versus client render. Vite
            handles bundling and browser caching.
          </p>
        </div>
        <ExpandDetails title="Why React and Vite (vs Vue or Svelte, vs Webpack)">
          <ul className="!mb-0">
            <li>
              <strong>React.</strong> Deep pool of editor integrations, controlled-input patterns, and types.{' '}
              <strong>Vue</strong> or <strong>Svelte</strong> can trim bundles; here breadth and tooling speed mattered
              more.
            </li>
            <li>
              <strong>Vite.</strong> Fast HMR and leaner config than typical legacy Webpack stacks.{' '}
              <code>vite-plugin-ruby</code> is the maintained Rails integration.
            </li>
          </ul>
        </ExpandDetails>

        <h2 id="styling">Tailwind CSS v4</h2>
        <p>
          <strong>Tailwind v4</strong> with the Vite plugin so tokens and utilities share the app build.{' '}
          <strong>@tailwindcss/forms</strong> and <strong>@tailwindcss/typography</strong> cover controls and long prose
          without tuning every spacing token by hand.
        </p>
        <ExpandDetails title="Utility-first vs CSS Modules vs component libraries">
          <p className="!mt-0">
            Utilities scale with what you ship; deleting a feature usually deletes its classes. CSS Modules excel at
            file-level scope; here shared tokens and Tailwind scales carry the layout.
          </p>
          <p>
            Big kits such as <strong>Material UI</strong> or <strong>Chakra</strong> speed widget-heavy products. This
            site prefers a small bespoke set so the shell does not default to generic dashboard chrome.
          </p>
        </ExpandDetails>

        <h2 id="data">PostgreSQL</h2>
        <p>
          <strong>PostgreSQL</strong> in every environment so types, constraints, and planner behavior match from local
          work through deploy. Models map via Active Record; migrations are schema history.
        </p>
        <ExpandDetails title="Why PostgreSQL, why not SQLite for everything">
          <p className="!mt-0">
            SQLite shines for embedded and offline-first cases. Across several Rails envs, locking, concurrency, and DDL
            gaps often show up only after production.
          </p>
          <ul className="!mb-0">
            <li>
              <strong>PostgreSQL.</strong> JSON and array types, solid ops tooling, and what hosts expect for production
              Rails.
            </li>
            <li>
              <strong>Same engine everywhere.</strong> Less "works on my machine" drift on plans, indexes, and locks.
            </li>
          </ul>
        </ExpandDetails>

        <h2 id="tools-sandbox">Tools and sandbox</h2>
        <p>
          Everything under <Link href="/tools">Tools</Link>, including the <Link href="/tools/sandbox">sandbox</Link>,
          runs in your browser only. Nothing sends your input to Nogrok to process remotely.
        </p>
        <p>
          Utilities use the <strong>Web Cryptography API</strong> for real crypto steps (for example digests) and plain{' '}
          <strong>TypeScript</strong> for the rest (Base64, JWT parsing, Unicode, and similar). Sensitive text stays on
          your device.
        </p>
        <p>
          The <Link href="/tools/sandbox">sandbox</Link> is a scratch space: <strong>Monaco</strong> editors and a live
          preview in a <strong>sandboxed iframe</strong> via <code>srcDoc</code>. CSP requires first-party Monaco bundles
          instead of remote script hosts.
        </p>
        <ExpandDetails title="Security tradeoffs (iframe + CSP)">
          <p className="!mt-0">
            Iframe <code>sandbox</code> trims navigation and pop-ups unless you widen it. That caps risk from pasted HTML
            and script but does not make untrusted input safe. Assume hostile code stays inside that document.
          </p>
          <p>
            Change CSP and sandbox together with care or the outer shell and inner frame fall out of sync.
          </p>
        </ExpandDetails>

        <h2 id="feature-flags">Feature flags</h2>
        <p>
          <strong>Feature flags</strong> flip behavior from the database so deploys are not required for every product
          tweak (rollouts, operator-only routes, and the like).
        </p>
        <p>
          <strong>Flipper</strong> keeps flags beside the rest of the data. The server decides on each request; any
          mirror in the UI is cosmetic only.
        </p>
        <p>
          A custom layer means bespoke storage, gates, cache behavior, admin UX, and review forever. Flipper already
          covers that for Rails so effort stays on features, not flag infrastructure.
        </p>

        <p className="!mt-12 border-t border-outline-variant/15 pt-8 font-mono text-xs text-on-surface-variant">
          Stack snapshot: Rails 8.1, Inertia Rails, React 19, TypeScript, Vite 8, Tailwind 4, PostgreSQL, Playwright
          end-to-end tests.
        </p>
      </article>
    </>
  )
}
